import { ErrorComponent } from "@components/common/ErrorComponent";
import { NotFound } from "@components/common/NotFound";
import { useAuthStore } from "@lib/auth/authStore";
import {
  redirectAuthenticatedFromLogin,
  redirectUnauthenticatedUser,
} from "@lib/auth/navigation-guards";
import type { CapabilityId, PermissionContext, UserType } from "@lib/permissions/types";
import { createRootRoute, Outlet } from "@tanstack/react-router";

/**
 * Route context type for TypeScript
 * This defines what will be available in context for all child routes
 */
export const routeOptions = {
  context: {
    /** Whether user is authenticated */
    isAuthenticated: false,
    /** Wildcard permission (*) grants full access */
    hasWildcard: false,
    /** Array of permission strings */
    permissions: [] as string[],
    /** User context from permissions (filiale, dept, manager status) */
    permissionContext: null as PermissionContext | null,
    /** ABAC: Capabilities derived from permissions + context */
    capabilities: [] as CapabilityId[],
    /** ABAC: User type derived from capabilities */
    userType: null as UserType | null,
    /** ABAC: Dashboard route based on capabilities */
    dashboardRoute: null as string | null,
  },
};

export const Route = createRootRoute({
  staticData: {},
  beforeLoad: async ({ location }) => {
    // Auth is bootstrapped by AuthProvider — just enforce redirect rules
    const { isLoggedIn } = useAuthStore.getState();
    const redirect = new URLSearchParams(location.search).get("redirect") ?? undefined;

    redirectUnauthenticatedUser({
      isLoggedIn,
      pathname: location.pathname,
      href: location.href,
    });

    redirectAuthenticatedFromLogin({
      isLoggedIn,
      pathname: location.pathname,
      redirect,
    });

    return {
      isAuthenticated: isLoggedIn,
      hasWildcard: false,
      permissions: [],
      permissionContext: null,
      capabilities: [],
      userType: null,
      dashboardRoute: null,
    };
  },
  notFoundComponent: NotFound,
  component: () => <Outlet />,
  errorComponent: ErrorComponent,
});
